import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import {
  addHalftimeHypothesis,
  commitHalftimeAdjustments,
  commitHalftimeNote,
  loadHalftimeContext,
  newHalftimeAdjustment,
  newHalftimeNote,
  startHalftimeNoteAudio,
  swapBoardTile,
} from '../../app/halftime';
import type { HalftimeContext, ManageRow } from '../../app/halftime';
import { commitUndo } from '../../app/discovery';
import { canRecord } from '../../app/microphone';
import { describeAudio, recorder } from '../../app/recorder';
import type { AudioDescription } from '../../app/recorder';
import { useRecorderView } from '../capture/useRecorder';
// HT-2's status controls dispatch the **same** `SetStatusOverride` the locked board's swipe does.
// R-2's asymmetry lives in the callers' ranges, not in the command: `swipeLeftOverride` can never
// produce `invalidated` (D-12), and this surface is one of the only two that can. A second wire
// would be a second place for that distinction to be forgotten.
import { setTileOverride } from '../../app/lockedBoard';
import { resolvePlayer } from '../../app/preMatch';
import {
  candidateFromFreeText,
  candidateFromTemplateEntry,
  resolveTemplateText,
} from '../../domain/setup';
import type { SlotValue } from '../../domain/setup';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import {
  applyAdjustmentSnapshot,
  canPromoteAnother,
  snapshotAdjustments,
} from '../../domain/halftime';
import type { AdjustmentCandidate, AdjustmentSnapshot } from '../../domain/halftime';
import type {
  HypothesisPhase,
  MatchNoteRow,
  MatchRow,
  StatusOverride,
  TalkPointSelection,
  TemplateEntry,
  TemplateRow,
} from '../../types';
import type { UndoStripRow } from '../../app/setPiece';

/**
 * The halftime hub's controller (build unit U-13a).
 *
 * Holds three things the hub cannot compute per render: the loaded context, **§7.3's adjustment
 * snapshot**, and **§7.5's pending note draft**. Everything else is derived.
 */

const MODULE = 'ui/halftime';

/**
 * AUD-4, said on the surface that asked — and said with the alternative in the same sentence.
 *
 * Distinct from `RE_ENABLE_HINT`, which the Flag bar raises **once** per session for flags. This one
 * fires on every refused press here, because the user has just pressed a control that did nothing
 * and a hint they dismissed an hour ago is not an answer. It names the way forward that still works
 * — the stakeholder's ruling was *"I want both text and voice available"*, so losing voice must
 * never read as losing the note.
 */
const NO_MICROPHONE_NOTICE =
  'No microphone — you can still type this note. Turn the microphone on for Touchline in Settings to record it.';

type Phase = 'loading' | 'ready' | 'missing' | 'failed';

export interface HalftimeState {
  readonly phase: Phase;
  readonly match: MatchRow | null;
  readonly opponent: string;
  /** §7.3 — the list **as the snapshot orders it**, not as a fresh rank would. */
  readonly ordered: readonly AdjustmentCandidate[];
  /** Ids currently promoted, for the tick on each row. */
  readonly promotedIds: readonly string[];
  /** `instructionText` per promoted hypothesis (INV-13 — optional, behind the pencil). */
  readonly instructionByHypothesis: Readonly<Record<string, string | null>>;
  readonly canPromote: boolean;
  readonly showAll: boolean;
  readonly undoRows: readonly UndoStripRow[];
  readonly stillInDiscovery: boolean;
  /** HT-8 — the committed note, if any. */
  readonly note: MatchNoteRow | undefined;
  /**
   * HT-8 (U-13c) — whether **this break's note** is what the recorder is recording right now.
   *
   * Read off the recorder's own published subject rather than held here, for the reason
   * `useRecorderView` gives: the recording outlives this component (AUD-3 finalizes it on `pagehide`
   * and on the `Kickoff 2H` transition, neither of which the sheet is mounted for), so a local
   * boolean would go stale the first time something other than the user's tap ended it.
   */
  readonly noteRecording: boolean;
  /**
   * §8.2 — what the committed note's audio actually *is*, or `null` when there is none.
   *
   * Derived from the stored `AudioBlob` rather than from *"we started a recording"*: §8.2 requires
   * that *"a dangling or incomplete blob is never presented as a normal note"*, and only the row can
   * say whether finalize ran and whether any bytes arrived. A caption that said "saved" because a
   * start succeeded is the proxy that requirement exists to forbid.
   */
  readonly noteAudio: AudioDescription | null;
  /**
   * §7.5 — text typed into the HT-8 sheet and not yet committed.
   *
   * **Owned here, not by the sheet.** The sheet is a `showModal()` dialog and therefore inerts the
   * `Start 2nd Half` control while it is open (see `HalftimeSheet`); if the draft died with the
   * sheet, §7.5's *"a note sheet holds unsaved text … the hold discloses rather than interrupts"*
   * would describe a state the user can never be in while able to reach the control. Holding it here
   * makes the disclosure reachable **and** makes *"the note sheet commits its text on the same
   * event"* implementable, because the transition can see the draft.
   */
  readonly noteDraft: string | null;
  readonly notice: string | null;
  /** HT-2 (U-13b) — *"hypotheses sorted by count with status"*, with board membership. */
  readonly manageRows: readonly ManageRow[];
  /** PRE-2 — the seeded library the add panel offers. */
  readonly library: readonly TemplateRow[];
  /**
   * A write is in flight.
   *
   * The sheets' controls read it so a double-tap cannot dispatch a second swap against the board the
   * first one is still changing — the single-flight queue would serialise them, but the *second*
   * would be planned against positions the user never saw.
   */
  readonly busy: boolean;
}

export interface HalftimeActions {
  readonly reload: () => void;
  readonly togglePromotion: (hypothesisId: string) => void;
  readonly setInstruction: (hypothesisId: string, text: string | null) => void;
  readonly setShowAll: (showAll: boolean) => void;
  readonly setNoteDraft: (text: string) => void;
  readonly saveNote: () => Promise<boolean>;
  /** HT-8 (U-13c) — start the note's voice recording, or stop the one that is running. */
  readonly toggleNoteRecording: () => Promise<void>;
  readonly undoFromStrip: (hypothesisId: string, element: HTMLElement) => void;
  readonly dismissNotice: () => void;
  /** §7.5 — flushes the pending note on the way out of halftime. Awaited by the transition. */
  readonly commitPending: () => Promise<void>;
  /** HT-2 / D-12 — set or clear a dismissal. `null` clears. */
  readonly setOverride: (hypothesisId: string, override: StatusOverride) => Promise<boolean>;
  /** LOCK-6 / DD-3 — put a pattern on the board; `outgoingId` names the displaced tile or is `null`. */
  readonly putOnBoard: (incomingId: string, outgoingId: string | null) => Promise<boolean>;
  /** PRE-3 — add a typed pattern with a tapped phase. */
  readonly addFreeText: (text: string, phase: HypothesisPhase) => Promise<boolean>;
  /** PRE-4 / DISC-5 — add a library entry, with any stub slots already picked. */
  readonly addFromLibrary: (
    templateId: string,
    entry: TemplateEntry,
    values: Readonly<Record<string, SlotValue>>,
  ) => Promise<boolean>;
}

export function useHalftime(matchId: string) {
  const [context, setContext] = useState<HalftimeContext | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [showAll, setShowAll] = useState(false);
  const [noteDraft, setNoteDraftState] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteAudio, setNoteAudio] = useState<AudioDescription | null>(null);
  const recorderView = useRecorderView();

  /**
   * §7.3 — *"The order is computed **on entry to the adjustments surface** and held for that
   * surface's visible lifetime."*
   *
   * A ref rather than state, because writing it must not itself schedule a render — and because the
   * *identity* of the snapshot is what "this visible lifetime" means. It is set exactly once, on the
   * first successful load; a reload during the break re-reads rows but **keeps** the snapshot, which
   * is what makes *"status changes restyle in place"* true rather than aspirational.
   *
   * HT-5 is rendered **inline on the hub** (§7.1), so "the adjustments surface" and "the hub" are the
   * same visible lifetime: leaving the hub — to the board, or out of halftime — is what recomputes.
   * Unmounting drops the ref, so re-entry recomputes with no explicit reset to remember.
   */
  const snapshot = useRef<AdjustmentSnapshot | null>(null);

  /** The draft the transition must flush, readable without waiting for a render (§7.5). */
  const pendingNote = useRef<string | null>(null);
  const loaded = useRef(false);

  /**
   * **Which reload is allowed to win.**
   *
   * Promotion is *"one tap per item"* (§7.3) and each tap commits then reloads, so two taps a few
   * hundred milliseconds apart put two `loadHalftimeContext` reads in flight at once. They are
   * independent transactions and **nothing orders their resolutions** — the earlier one can settle
   * last and overwrite the later one's state with a strictly older snapshot.
   *
   * **Stated honestly: this guard was reasoned, not reproduced.** It came out of investigating a
   * `scripts/verify-halftime.mjs` failure that turned out to be the *pass's* own fixed-timeout wait,
   * not this — so the browser evidence is for the check, and the guard stands on the hazard itself.
   * The hazard is nonetheless real and not hypothetical: nothing orders two concurrent reads, and if
   * the older one lands last the hub renders a **stale tick count that never self-corrects**, which
   * would let the user try to promote past `TALKPOINT_COUNT` and be refused by a bound they could not
   * see they had reached. `fake-indexeddb` resolves too promptly for a Node test to expose it either.
   *
   * The mechanism, rather than a delay: each read takes a ticket, and a read whose ticket is stale by
   * the time it resolves **drops its result**. Last request wins, never last response.
   */
  const request = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    const ticket = (request.current += 1);
    const next = await loadHalftimeContext(matchId);
    if (ticket !== request.current) return;
    if (next === undefined) {
      setPhase('missing');
      return;
    }
    snapshot.current ??= snapshotAdjustments(next.candidates);
    setContext(next);
    setPhase('ready');
  }, [matchId]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void refresh().catch((cause: unknown) => {
      log.error(MODULE, 'the halftime hub could not be loaded', {
        matchId,
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, [matchId, refresh]);

  const reload = useCallback((): void => {
    void refresh().catch((cause: unknown) => {
      log.error(MODULE, 'the halftime hub could not be reloaded', {
        matchId,
        message: cause instanceof Error ? cause.message : String(cause),
      });
    });
  }, [matchId, refresh]);

  // ── HT-5 ───────────────────────────────────────────────────────────────────

  /**
   * The promoted selection, keyed by the hypothesis it came from.
   *
   * Derived from committed rows rather than held as view state, so SES-2 holds on this surface too:
   * a tick appears because a `TalkPoint` row exists, never because a tap was seen.
   */
  const promoted = context?.promoted ?? [];
  const promotedIds = promoted
    .map((row) => row.sourceHypothesisId)
    .filter((id): id is string => id !== null);
  const instructionByHypothesis: Record<string, string | null> = {};
  for (const row of promoted) {
    if (row.sourceHypothesisId !== null) {
      instructionByHypothesis[row.sourceHypothesisId] = row.instructionText;
    }
  }

  const writeSelection = useCallback(
    (ordered: readonly TalkPointSelection[]): void => {
      if (context === null) return;
      void commitHalftimeAdjustments(context.match.id, ordered).then(
        (result) => {
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          reload();
        },
        (cause: unknown) => {
          log.error(MODULE, 'HT-5: the adjustment selection could not be written', {
            matchId: context.match.id,
            message: cause instanceof Error ? cause.message : String(cause),
          });
          setNotice('That could not be saved. Nothing was changed.');
        },
      );
    },
    [context, reload],
  );

  /**
   * HT-5 — *"Promotion is one tap per item."* The same tap un-promotes.
   *
   * Both directions rewrite the **whole ordered selection**, which is §10.2's rank rule: rank is the
   * index the user sees, so removing entry 2 of 3 renders `1..2` rather than leaving a `{1,3}` gap
   * that `CompleteDistillation`'s INV-6 postcondition would later abort on.
   */
  const togglePromotion = useCallback(
    (hypothesisId: string): void => {
      if (context === null) return;
      const existing = context.promoted;
      const already = existing.some((row) => row.sourceHypothesisId === hypothesisId);

      if (already) {
        writeSelection(
          existing
            .filter((row) => row.sourceHypothesisId !== hypothesisId)
            .map(({ rank: _rank, ...rest }) => rest),
        );
        return;
      }

      if (!canPromoteAnother(existing.length)) {
        setNotice(
          `You can take at most ${String(config.TALKPOINT_COUNT)} adjustments into the second half.`,
        );
        return;
      }

      writeSelection([
        ...existing.map(({ rank: _rank, ...rest }) => rest),
        newHalftimeAdjustment(context.match.id, hypothesisId, null),
      ]);
    },
    [context, writeSelection],
  );

  /** INV-13 — the optional pencil. Editing an unpromoted row is not a promotion path. */
  const setInstruction = useCallback(
    (hypothesisId: string, text: string | null): void => {
      if (context === null) return;
      const trimmed = text === null || text.trim() === '' ? null : text.trim();
      writeSelection(
        context.promoted.map(({ rank: _rank, ...rest }) =>
          rest.sourceHypothesisId === hypothesisId ? { ...rest, instructionText: trimmed } : rest,
        ),
      );
    },
    [context, writeSelection],
  );

  // ── HT-8 ───────────────────────────────────────────────────────────────────

  const setNoteDraft = useCallback((text: string): void => {
    pendingNote.current = text;
    setNoteDraftState(text);
  }, []);

  /**
   * Writes the note and clears the pending draft.
   *
   * @returns whether anything was committed. `false` for an empty draft on a match with no note,
   *   which is HT-8's *"neither"* case and not a failure — §7.3's "no exit nag" reasoning applies
   *   equally here: opening the sheet and typing nothing must cost nothing.
   */
  const flushNote = useCallback(async (): Promise<boolean> => {
    if (context === null) return false;
    const draft = pendingNote.current;
    if (draft === null) return false;

    const text = draft.trim();
    if (text === '' && context.note === undefined) {
      pendingNote.current = null;
      setNoteDraftState(null);
      return false;
    }

    const row: MatchNoteRow =
      context.note === undefined
        ? { ...newHalftimeNote(context.match, Date.now()), text }
        : { ...context.note, text: text === '' ? null : text };

    const result = await commitHalftimeNote(row);
    if (!result.ok) {
      setNotice(result.message);
      return false;
    }
    pendingNote.current = null;
    setNoteDraftState(null);
    reload();
    return true;
  }, [context, reload]);

  const saveNote = useCallback(
    (): Promise<boolean> =>
      flushNote().catch((cause: unknown) => {
        log.error(MODULE, 'HT-8: the halftime note could not be saved', {
          matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setNotice('That note could not be saved.');
        return false;
      }),
    [flushNote, matchId],
  );

  // ── HT-8's voice half — build unit U-13c ───────────────────────────────────

  /**
   * HT-8 — one control, both directions, exactly as FLAG-1's Flag bar has.
   *
   * `startHalftimeNoteAudio` routes through `RecorderService.pressOrStart`, so a press while
   * *anything* is recording stops it — including a flag started on the board seconds before the Half
   * hold. That is the same rule the bar follows and it means the sheet needs no mode of its own.
   *
   * ── Three outcomes, three different sentences ───────────────────────────────
   *
   * A press that did nothing and said nothing is indistinguishable from a broken control (NFR-8), so
   * every arm speaks. `refused` is the one that needs care: it covers both *"there is no
   * microphone"* and *"the domain said no"*, and those are different problems with different
   * remedies, so the microphone is asked which it was.
   *
   * The draft is cleared **only** on a successful start, because that is the commit that carried it:
   * `handleStartMatchNoteAudio` writes the typed text through the same planner *Done* uses. Leaving
   * it set would make §7.5's disclosure claim there is unsaved text when there is not.
   */
  const toggleNoteRecording = useCallback(async (): Promise<void> => {
    if (context === null) return;
    setNotice(null);
    try {
      const action = await startHalftimeNoteAudio(context.match, context.note, pendingNote.current);
      if (action.kind === 'refused') {
        setNotice(
          canRecord(recorder.micState())
            ? 'That voice note could not be started. Nothing was changed.'
            : NO_MICROPHONE_NOTICE,
        );
        return;
      }
      if (action.kind === 'started') {
        pendingNote.current = null;
        setNoteDraftState(null);
      }
      if (action.kind === 'stopped' && action.outcome.bytes === 0) {
        // §8.2's honest degradation, said at the moment it is knowable. The note row survives with
        // its text; only the audio is missing, and `noteAudio` below will keep saying so.
        setNotice('Nothing was captured for that voice note.');
      }
      reload();
    } catch (cause: unknown) {
      log.error(MODULE, 'HT-8: the voice note control failed', {
        matchId: context.match.id,
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setNotice('That voice note could not be started. Nothing was changed.');
    }
  }, [context, reload]);

  /**
   * §8.2's caption for the committed note's audio, re-read whenever the id changes.
   *
   * Its own transaction rather than a widening of `loadHalftimeContext`'s scope: `audioBlobs` is the
   * one table the hub otherwise never touches, and a blob row read inside the hub's transaction
   * would put the byte column in the same scope as HT-5's ranking read for no gain. Not a cadence —
   * it runs when an id appears or changes (NFR-5).
   */
  useEffect(() => {
    const id = context?.note?.audioBlobId ?? null;
    const minute = context?.note?.matchMinute ?? 0;
    if (id === null) {
      setNoteAudio(null);
      return;
    }
    let live = true;
    void describeAudio(id, minute).then(
      (description) => {
        if (live) setNoteAudio(description);
      },
      (cause: unknown) => {
        // Surfaced, never swallowed: an unreadable blob row and an absent one look identical to the
        // user and only one of them means something was lost (NFR-8).
        log.error(MODULE, 'HT-8: the note’s audio could not be described', {
          audioBlobId: id,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        if (live) setNoteAudio(null);
      },
    );
    return () => {
      live = false;
    };
  }, [context?.note?.audioBlobId, context?.note?.matchMinute]);

  /**
   * §7.5 — *"the note sheet commits its text on the same event"* as `Kickoff 2H`.
   *
   * Awaited by the transition's press handler **before** it dispatches, because the note command
   * refuses outside `halftime` (`ERR_NOT_AT_HALFTIME`): flushing after the transition committed
   * would lose exactly the text this clause exists to preserve.
   */
  const commitPending = useCallback(async (): Promise<void> => {
    if (pendingNote.current === null) return;
    await saveNote();
  }, [saveNote]);

  // ── HT-2 / PRE-3 — build unit U-13b ────────────────────────────────────────

  /**
   * Every U-13b write goes through here, and the reason is the reload.
   *
   * A status change, a swap and an add all alter the same list the sheet is rendering, so each must
   * be followed by a re-read or the next control the user touches is planned against what they can
   * see rather than what is stored. Routing them through one place also gives `busy` one owner —
   * `finally`, so a rejection releases the controls rather than wedging the sheet.
   *
   * @returns whether the write committed. The sheets use it to decide whether to return to their
   *   list; a failed write must leave the user where they were, looking at the notice.
   */
  const run = useCallback(
    async (what: string, write: () => Promise<{ ok: boolean; message?: string }>): Promise<boolean> => {
      // Clear first, so a notice on screen always describes THIS action. Without it a failed swap's
      // message outlives the swap and sits over the sheet through every later success — on the one
      // surface in the product where the user has three minutes and is reading, not studying.
      setNotice(null);
      setBusy(true);
      try {
        const result = await write();
        if (!result.ok) {
          setNotice(result.message ?? 'That could not be saved.');
          return false;
        }
        reload();
        return true;
      } catch (cause: unknown) {
        log.error(MODULE, `${what} failed`, {
          matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setNotice('That could not be saved. Nothing was changed.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [matchId, reload],
  );

  /**
   * HT-2 / D-12 — *"toggle `disproved`, set and clear `invalidated`"*.
   *
   * The value is decided by the caller because HT-2's controls are two independent toggles over one
   * field (§3.3's dismissal dimension holds one value at a time), which is a different rule from
   * LIVE-5's single swipe. Both dispatch the same command; only their *ranges* differ, and that is
   * R-2's ratified asymmetry rather than an inconsistency to iron out.
   */
  const setOverride = useCallback(
    (hypothesisId: string, override: StatusOverride): Promise<boolean> => {
      if (context === null) return Promise.resolve(false);
      return run('HT-2: the status override', () =>
        setTileOverride(context.match.id, hypothesisId, override),
      );
    },
    [context, run],
  );

  /** LOCK-6 / DD-3 — the composition change. See `SwapBoardTileCommand` for the two paths. */
  const putOnBoard = useCallback(
    (incomingId: string, outgoingId: string | null): Promise<boolean> => {
      if (context === null) return Promise.resolve(false);
      return run('LOCK-6: the board swap', () =>
        swapBoardTile(context.match.id, incomingId, outgoingId),
      );
    },
    [context, run],
  );

  /** PRE-3 — free text plus a tapped phase. §3.2's source 2 derives the bucket from the phase. */
  const addFreeText = useCallback(
    (text: string, phase: HypothesisPhase): Promise<boolean> => {
      if (context === null) return Promise.resolve(false);
      const trimmed = text.trim();
      if (trimmed === '') return Promise.resolve(false);
      return run('PRE-3: the typed pattern', () =>
        addHalftimeHypothesis(context.match.id, candidateFromFreeText(newId(), trimmed, phase)),
      );
    },
    [context, run],
  );

  /**
   * PRE-4 / DISC-5 — a library entry, with its stub slots resolved.
   *
   * The player is created **first and separately**, by `CreatePlayer`, because `HalftimeAddOp` cannot
   * emit `insertPlayer` — the same two-command shape Discovery's add uses, for the same reason
   * `CreatePlayerCommand` gives: an upsert inside the add's transaction would resolve the player id
   * where the already-built `HypothesisRow` could no longer be told about it.
   */
  const addFromLibrary = useCallback(
    async (
      templateId: string,
      entry: TemplateEntry,
      values: Readonly<Record<string, SlotValue>>,
    ): Promise<boolean> => {
      if (context === null) return false;
      const resolved = resolveTemplateText(entry.text, entry.slots ?? [], values);
      if (!resolved.ok) {
        setNotice(resolved.message);
        return false;
      }

      let playerId: string | null = null;
      if (resolved.value.shirtNumber !== null) {
        const player = await resolvePlayer(
          context.match.teamId,
          resolved.value.shirtNumber,
          resolved.value.foot,
        );
        if (!player.ok) {
          setNotice(player.message);
          return false;
        }
        playerId = player.id;
      }

      return run('PRE-4: the library pattern', () =>
        addHalftimeHypothesis(
          context.match.id,
          candidateFromTemplateEntry(newId(), templateId, entry, resolved.value.text, playerId),
        ),
      );
    },
    [context, run],
  );

  // ── LIVE-6, on the surface §5.6 routes it to ───────────────────────────────

  /**
   * §7.1 — *"an increment recorded seconds before the Half tap remains undoable for its full
   * window."* The same `UndoIncrement` command the board and Discovery dispatch, carrying the same
   * `hypothesisId` — this hub is a *router to a per-hypothesis affordance*, never a global undo.
   */
  const undoFromStrip = useCallback(
    (hypothesisId: string, element: HTMLElement): void => {
      if (context === null) return;
      void commitUndo(context.match.id, hypothesisId, () => {
        delete element.dataset.ack;
        void element.offsetWidth;
        element.dataset.ack = 'true';
        window.setTimeout(() => {
          delete element.dataset.ack;
        }, config.ACK_PERSIST_MIN_MS);
      }).then(
        (result) => {
          if (!result.ok) setNotice(result.message);
          reload();
        },
        (cause: unknown) => {
          log.error(MODULE, 'LIVE-6: the undo could not be applied', {
            matchId: context.match.id,
            message: cause instanceof Error ? cause.message : String(cause),
          });
          setNotice('That could not be undone.');
        },
      );
    },
    [context, reload],
  );

  const ordered =
    context === null || snapshot.current === null
      ? []
      : applyAdjustmentSnapshot(snapshot.current, context.candidates);

  const state: HalftimeState = {
    phase,
    match: context?.match ?? null,
    opponent: context?.opponent ?? '',
    ordered,
    promotedIds,
    instructionByHypothesis,
    canPromote: canPromoteAnother(promoted.length),
    showAll,
    undoRows: context?.undoRows ?? [],
    stillInDiscovery: context?.stillInDiscovery ?? false,
    note: context?.note,
    // Scoped to *this* match as well as to the subject: the recorder is a singleton and one badly
    // timed navigation would otherwise let another match's recording light this sheet.
    noteRecording:
      recorderView.recording !== null &&
      recorderView.recording.subject.kind === 'matchNote' &&
      recorderView.recording.matchId === context?.match.id,
    noteAudio,
    noteDraft,
    notice,
    manageRows: context?.manageRows ?? [],
    library: context?.library ?? [],
    busy,
  };

  const actions: HalftimeActions = {
    reload,
    togglePromotion,
    setInstruction,
    setShowAll,
    setNoteDraft,
    saveNote,
    toggleNoteRecording,
    undoFromStrip,
    dismissNotice: useCallback(() => {
      setNotice(null);
    }, []),
    commitPending,
    setOverride,
    putOnBoard,
    addFreeText,
    addFromLibrary,
  };

  return { state, actions };
}
